import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { collection, getDocs, updateDoc, deleteDoc, doc, setDoc } from 'firebase/firestore';

const ROLES = ['Administrador', 'Suscriptor', 'Comercial', 'Productor', 'Agente', 'Finanzas'];

const AdminEquipo = () => {
    const navigate = useNavigate();
    const [colaboradores, setColaboradores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [localError, setLocalError] = useState(null);
    
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(null);
    
    // Formulario hidratado con la nueva propiedad 'tieneAcceso'
    const [formData, setFormData] = useState({
        uid: '', 
        nombre: '', 
        email: '',
        rol: 'Productor',
        activo: true,
        accesoFull: true,
        tieneAcceso: true // ◄ NUEVA PROPIEDAD: Habilita o restringe el acceso de login
    });

    const colabRef = collection(db, 'public/data/colaboradores');

    // --- CARGA DE DATOS ---
    const fetchData = async () => {
        setLoading(true);
        setLocalError(null);
        try {
            const snap = await getDocs(colabRef);
            const data = snap.docs.map(doc => {
                const docData = doc.data();
                return { 
                    id: doc.id, 
                    ...docData,
                    nombre: docData.nombre || docData.userName || 'S/D',
                    tieneAcceso: docData.tieneAcceso !== undefined ? docData.tieneAcceso : true
                };
            });
            // Ordenar alfabéticamente en memoria
            data.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
            setColaboradores(data);
        } catch (error) { 
            console.error("Error cargando colaboradores:", error); 
            setLocalError("No se pudieron cargar los colaboradores de la base de datos.");
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { 
        fetchData(); 
    }, []);

    // --- CONTROL DE VENTANA MODAL ---
    const handleOpenModal = (item = null) => {
        setLocalError(null);
        if (item) {
            setFormData({
                uid: item.id,
                nombre: item.nombre || item.userName || '',
                email: item.email || '',
                rol: item.rol || 'Productor',
                activo: item.activo !== undefined ? item.activo : true,
                accesoFull: item.accesoFull !== undefined ? item.accesoFull : true,
                tieneAcceso: item.tieneAcceso !== undefined ? item.tieneAcceso : true
            });
            setIsEditing(item.id);
        } else {
            setFormData({ 
                uid: '', 
                nombre: '', 
                email: '', 
                rol: 'Productor', 
                activo: true, 
                accesoFull: true,
                tieneAcceso: true 
            });
            setIsEditing(null);
        }
        setShowModal(true);
    };

    // --- GUARDADO DE COLABORADOR ---
    const handleSave = async (e) => {
        e.preventDefault();
        setLocalError(null);
        try {
            const nameValue = formData.nombre.trim();
            const targetUid = formData.uid.trim();
            
            // Si requiere acceso al sistema, validamos UID y Email
            if (formData.tieneAcceso && !targetUid) {
                setLocalError("Por favor, ingrese el UID de Firebase Authentication para habilitar el inicio de sesión.");
                return;
            }

            // Construcción del payload limpio de guardado
            const payload = { 
                nombre: nameValue,
                userName: nameValue,
                email: formData.tieneAcceso ? formData.email.trim() : (formData.email.trim() || null),
                rol: formData.rol,
                activo: formData.activo,
                tieneAcceso: formData.tieneAcceso,
                // Si no tiene acceso, revocamos el accesoFull por defecto
                accesoFull: formData.tieneAcceso ? formData.accesoFull : false
            };
            
            if (isEditing) {
                // Modo Edición
                await setDoc(doc(db, 'public/data/colaboradores', isEditing), {
                    ...payload,
                    id: isEditing
                }, { merge: true });
            } else {
                // Modo Creación: Si no tiene acceso, generamos un ID automático de Firestore
                const finalId = formData.tieneAcceso ? targetUid : doc(collection(db, 'public/data/colaboradores')).id;
                
                await setDoc(doc(db, 'public/data/colaboradores', finalId), {
                    ...payload,
                    id: finalId
                });
            }
            
            setShowModal(false);
            fetchData();
        } catch (error) { 
            console.error("Error al guardar el colaborador:", error);
            setLocalError("Error crítico al intentar persistir los cambios del colaborador.");
        }
    };

    // --- ELIMINACIÓN DE COLABORADOR ---
    const handleDelete = async (id) => {
        setLocalError(null);
        if (window.confirm("¿Está seguro de eliminar este colaborador? Se borrarán sus datos del listado de responsables.")) {
            try {
                await deleteDoc(doc(db, 'public/data/colaboradores', id));
                fetchData();
            } catch (error) {
                console.error("Error eliminando colaborador:", error);
                setLocalError("No se pudo eliminar el colaborador seleccionado.");
            }
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const nextState = { ...prev, [name]: type === 'checkbox' ? checked : value };
            
            // Si apaga el acceso al sistema, limpiamos las propiedades de autenticación
            if (name === 'tieneAcceso' && !checked) {
                nextState.uid = '';
                nextState.accesoFull = false;
            }
            return nextState;
        });
    };

    return (
        <div className="p-8 bg-slate-50 min-h-screen font-sans text-slate-800 text-left transition-all duration-300">
            <div className="max-w-6xl mx-auto">
                
                {/* CABECERA */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4 text-left select-none">
                    <div className="text-left">
                        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Equipo y Accesos</h1>
                        <p className="text-slate-500 mt-1.5 font-medium text-sm text-left">Configure los responsables de cuentas y perfiles con o sin acceso de inicio de sesión.</p>
                    </div>
                    <button onClick={() => navigate('/AdminDashboard')} className="bg-white border border-slate-300 text-slate-700 px-5 py-2 rounded-xl font-bold shadow-sm hover:bg-slate-50 transition text-sm active:scale-95 transform">
                        Volver
                    </button>
                </div>

                {/* ALERTA DE ERROR LOCAL */}
                {localError && (
                    <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-2xl font-bold border-l-4 border-red-500 shadow-sm animate-fade-in flex items-center gap-3">
                        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        {localError}
                    </div>
                )}

                {/* BOTÓN ALTA RÁPIDA */}
                <div className="flex justify-end mb-4">
                    <button onClick={() => handleOpenModal()} className="bg-pink-600 text-white px-5 py-3 rounded-xl font-black uppercase tracking-wider hover:bg-pink-700 text-xs flex gap-2 items-center transition-all shadow-lg shadow-pink-100 active:scale-95 transform">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        + Nuevo Colaborador
                    </button>
                </div>

                {/* TABLA PRINCIPAL */}
                <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 text-slate-500 uppercase font-black text-[10px] tracking-wider border-b border-slate-200 select-none">
                                <tr>
                                    <th className="px-6 py-4">Colaborador</th>
                                    <th className="px-6 py-4">Email / UID de Login</th>
                                    <th className="px-6 py-4">Rol</th>
                                    <th className="px-6 py-4 text-center">Permisos</th>
                                    <th className="px-6 py-4 text-center">Estado</th>
                                    <th className="px-6 py-4 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-sm">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-16 italic text-slate-400 font-bold animate-pulse uppercase tracking-widest text-xs">
                                            Sincronizando equipo de trabajo...
                                        </td>
                                    </tr>
                                ) : colaboradores.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-16 text-slate-400 italic font-medium">
                                            No se encontraron colaboradores registrados en el sistema.
                                        </td>
                                    </tr>
                                ) : (
                                    colaboradores.map(colab => (
                                        <tr key={colab.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4 font-bold text-slate-900">{colab.nombre}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col text-left">
                                                    {colab.tieneAcceso ? (
                                                        <>
                                                            <span className="text-slate-600 font-medium">{colab.email || 'Sin correo registrado'}</span>
                                                            <span className="text-[9px] text-slate-400 font-mono mt-0.5 select-all">UID: {colab.id}</span>
                                                        </>
                                                    ) : (
                                                        <span className="text-xs font-bold text-slate-400 italic select-none">
                                                            Sólo Registro (Sin acceso de login)
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 select-none">
                                                <span className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase border tracking-tight
                                                    ${colab.rol === 'Administrador' ? 'bg-purple-50 text-purple-700 border-purple-100' : 
                                                      colab.rol === 'Suscriptor' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                                      colab.rol === 'Comercial' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-50 text-slate-600 border-slate-100'}`}>
                                                    {colab.rol}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center select-none">
                                                {colab.tieneAcceso ? (
                                                    <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${
                                                        colab.accesoFull 
                                                            ? 'text-emerald-700 bg-emerald-50 border-emerald-100' 
                                                            : 'text-blue-700 bg-blue-50 border-blue-100'
                                                    }`}>
                                                        {colab.accesoFull ? 'Full / Carga' : 'Lectura'}
                                                    </span>
                                                ) : (
                                                    <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border text-slate-400 bg-slate-100 border-slate-200">
                                                        Bloqueado
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center select-none">
                                                <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase ${
                                                    colab.activo ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                                                }`}>
                                                    {colab.activo ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-1.5 select-none">
                                                    <button onClick={() => handleOpenModal(colab)} className="text-slate-400 hover:text-pink-600 p-2 hover:bg-pink-50 rounded-xl transition-all" title="Editar"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                                    <button onClick={() => handleDelete(colab.id)} className="text-slate-300 hover:text-red-500 p-2 hover:bg-rose-50 rounded-xl transition-all" title="Eliminar"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* MODAL DE EDICIÓN / DETALLES */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-[1100] backdrop-blur-sm animate-fade-in text-left font-sans">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col border border-gray-100 transition-all duration-300">
                        
                        {/* Cabecera */}
                        <div className="bg-pink-600 px-6 py-4 flex justify-between items-center text-white shrink-0 select-none">
                            <h3 className="font-extrabold text-sm uppercase tracking-wider">{isEditing ? 'Configurar Perfil' : 'Alta de Colaborador'}</h3>
                            <button onClick={() => setShowModal(false)} className="hover:rotate-90 transition-transform font-bold text-lg p-1 hover:bg-pink-700 rounded-lg">✕</button>
                        </div>
                        
                        {/* Formulario */}
                        <form onSubmit={handleSave} className="p-6 space-y-5 text-left">
                            
                            {/* SWITCH DE ACCESO AL SISTEMA */}
                            <div className={`p-4 rounded-xl border transition-all duration-300 ${formData.tieneAcceso ? 'bg-pink-50/40 border-pink-200' : 'bg-slate-50 border-slate-200'}`}>
                                <label className="flex items-center justify-between cursor-pointer select-none">
                                    <div className="flex flex-col text-left">
                                        <span className={`text-xs font-black uppercase tracking-wider ${formData.tieneAcceso ? 'text-pink-800' : 'text-slate-500'}`}>¿Tiene acceso de inicio de sesión?</span>
                                        <span className="text-[9px] font-bold text-slate-400 mt-1 leading-normal italic">Marque para requerir UID de autenticación en Firebase.</span>
                                    </div>
                                    <div className="relative inline-flex items-center scale-110 ml-4 shrink-0">
                                        <input 
                                            type="checkbox" 
                                            name="tieneAcceso" 
                                            checked={formData.tieneAcceso || false} 
                                            onChange={handleChange} 
                                            className="sr-only peer" 
                                        />
                                        <div className="w-10 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-pink-600 shadow-sm"></div>
                                    </div>
                                </label>
                            </div>

                            {/* Campo UID Condicional */}
                            {!isEditing && formData.tieneAcceso && (
                                <div className="text-left animate-fade-in">
                                    <label className="text-[10px] font-black text-pink-600 uppercase tracking-widest block mb-1.5 pl-1 select-none">UID de Firebase (Copiado de Auth) *</label>
                                    <input type="text" name="uid" value={formData.uid} onChange={handleChange} className="w-full border border-pink-100 rounded-xl px-4 py-2.5 text-xs text-slate-700 outline-none bg-pink-50/10 font-mono tracking-wide focus:border-pink-500 focus:ring-1 focus:ring-pink-100 transition-all h-[44px]" required placeholder="Ej: lct430Iu..." />
                                </div>
                            )}

                            <div className="text-left">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 pl-1 select-none">Nombre Completo *</label>
                                <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none bg-white focus:border-pink-500 focus:ring-1 focus:ring-pink-100 transition-all h-[44px]" required placeholder="Nombre y Apellido" />
                                <p className="text-[9px] text-slate-400 mt-1.5 italic font-medium pl-1 select-none">Este nombre se utilizará en todos los reportes, firmas de pólizas y tableros.</p>
                            </div>

                            <div className="text-left">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 pl-1 select-none">Email de Acceso {formData.tieneAcceso && '*'}</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white focus:border-pink-500 focus:ring-1 focus:ring-pink-100 transition-all h-[44px]" required={formData.tieneAcceso} placeholder="usuario@seguros.com" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="text-left">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 pl-1 select-none">Rol</label>
                                    <select name="rol" value={formData.rol} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none bg-white focus:border-pink-500 focus:ring-1 focus:ring-pink-100 transition-all h-[44px]">
                                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </div>
                                <div className="flex items-center pt-5 select-none pl-3">
                                    <label className="flex items-center cursor-pointer group">
                                        <input type="checkbox" name="activo" checked={formData.activo} onChange={handleChange} className="w-4 h-4 text-pink-600 rounded border-slate-300 focus:ring-pink-500 cursor-pointer" />
                                        <span className="ml-2 text-[10px] font-black uppercase text-slate-500 group-hover:text-pink-600 transition-colors tracking-widest">Habilitado</span>
                                    </label>
                                </div>
                            </div>

                            {/* Sección de Permisos de Acceso Condicional */}
                            {formData.tieneAcceso && (
                                <div className="p-4 bg-slate-50 rounded-xl border border-dashed border-slate-300 animate-fade-in select-none text-left">
                                    <label className="flex items-start cursor-pointer group">
                                        <input type="checkbox" name="accesoFull" checked={formData.accesoFull} onChange={handleChange} className="w-4 h-4 text-pink-600 rounded border-slate-300 focus:ring-pink-500 mt-0.5 cursor-pointer" />
                                        <div className="ml-3 text-left">
                                            <span className="block text-xs font-black text-slate-800 uppercase tracking-widest group-hover:text-pink-600 transition-colors">Acceso Operativo Full</span>
                                            <span className="block text-[9px] text-slate-500 leading-normal mt-1 font-bold italic">Habilita creación y edición en todos los tableros del sistema. Si se desmarca, el usuario operará en modo Sólo Lectura.</span>
                                        </div>
                                    </label>
                                </div>
                            )}

                            {/* Botonera de Acciones */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 select-none">
                                <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs hover:bg-slate-50 transition transform active:scale-95 shadow-sm">Cancelar</button>
                                <button type="submit" className="px-6 py-2.5 rounded-full bg-pink-600 text-white font-bold uppercase text-xs hover:bg-pink-700 transition shadow-lg shadow-pink-100 transform active:scale-95">Guardar Perfil</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminEquipo;